import React, { useState, useCallback, useEffect } from "react";
import ti18n from "i18n!nls/MyLibraryi18n";
import capsuleTi18n from "i18n!nls/CapsuleCardi18n";
import PersistentModels from "../services/PersistentModels";
import TrebbleClientAPI from "../models/helper/TrebbleClientAPI";
import LocalStorageHelper from "../models/helper/LocalStorageHelper";
import useDefaultTrebbleAntDTokenConfig from "./helper/useDefaultTrebbleAntDTokenConfig";
import PropTypes from "prop-types";
import InfiniteList, { ListItem, ListItemMeta, Skeleton } from "./common/InfiniteList";
import Button from "./common/Button";
import TrebbleSegmented from "./common/TrebbleSegmented";
import moment from "moment";

const WORKSPACE_FILTER_ALL = "all";
const WORKSPACE_FILTER_OWNED = "ownedByMe";

const VisibilityBadge = ({ visibility }) => {
    const isShared = visibility !== "private";
    const label = isShared
        ? window.getI18n(capsuleTi18n, "SHARED")
        : window.getI18n(capsuleTi18n, "PRIVATE");
    const iconClass = isShared ? "pe-7s-users" : "pe-7s-lock";

    return (
        <span
            className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5"
            style={{
                border: "1px solid",
                borderColor: isShared ? "#1890ff" : "#d9d9d9",
                color: isShared ? "#1890ff" : "#8c8c8c",
            }}
            title={label}
        >
            <i className={iconClass} />
            {label}
        </span>
    );
};

VisibilityBadge.propTypes = {
    visibility: PropTypes.string,
};

const UserEditHistoryList = (props) => {
    const { inDarkMode } = useDefaultTrebbleAntDTokenConfig();
    const isInWorkspace = LocalStorageHelper.getInstance().isInWorkspaceContext();
    var savedFilter = LocalStorageHelper.getInstance().getWorkspaceProjectFilter();
    const [workspaceFilter, setWorkspaceFilter] = useState(savedFilter === WORKSPACE_FILTER_OWNED ? savedFilter : WORKSPACE_FILTER_ALL);

    useEffect(() => {
        var savedFilter = LocalStorageHelper.getInstance().getWorkspaceProjectFilter();
        if (savedFilter === WORKSPACE_FILTER_OWNED) {
            setWorkspaceFilter(savedFilter);
        }
    }, []);

    const fetchSequencerProjects = useCallback(
        (pageIndex, pageSize) => {
            if (isInWorkspace) {
                const workspaceId = LocalStorageHelper.getInstance().getCurrentWorkspaceId();
                return TrebbleClientAPI.getInstance().listWorkspaceProjects({
                    workspaceId: workspaceId,
                    ownedByMe: workspaceFilter === WORKSPACE_FILTER_OWNED,
                    searchKeyword: props.searchFieldValue,
                    pageOffset: pageIndex,
                    numberOfResults: pageSize,
                    referenceProjectOnly: false,
                });
            }
            return TrebbleClientAPI.getInstance().getSequencerProjects(
                props.searchFieldValue,
                pageIndex,
                pageSize,
                false,
            );
        },
        [isInWorkspace, workspaceFilter, props.searchFieldValue],
    );

    const getEmptyText = () => {
        return window.getI18n(ti18n, "NO_EDITS_FOUND");
    };

    const onFilterChange = (value) => {
        setWorkspaceFilter(value);
        LocalStorageHelper.getInstance().setWorkspaceProjectFilter(
            value === WORKSPACE_FILTER_ALL ? null : value
        );
    };

    const filterOptions = [
        { label: window.getI18n(ti18n, "ALL"), value: WORKSPACE_FILTER_ALL },
        { label: window.getI18n(ti18n, "OWNED_BY_ME_TAB"), value: WORKSPACE_FILTER_OWNED },
    ];

    // Use a unique key that includes the filter so the InfiniteList remounts when filter changes
    const listKey = `editHistoryList-${workspaceFilter}-${props.searchFieldValue || ""}`;

    return (
        <div>
            {isInWorkspace && (
                <div className="px-4 py-2 m-auto max-w-3xl">
                    <TrebbleSegmented
                        options={filterOptions}
                        value={workspaceFilter}
                        onChange={onFilterChange}
                        block
                    />
                </div>
            )}
            <InfiniteList
                key={listKey}
                containerId="editHistoryListContainer"
                containerHeightMargin={isInWorkspace ? props.containerHeightMargin + 48 : props.containerHeightMargin}
                containerClassname={`overflow-auto px-4 py-0 ${inDarkMode ? "" : "bg-white"} m-auto max-w-3xl`}
                locale={{ emptyText: getEmptyText() }}
                fetchData={fetchSequencerProjects}
                loader={
                    <div>
                        <Skeleton className="py-5" paragraph={{ rows: 1, width: "80%" }} active />
                        <Skeleton className="py-5" paragraph={{ rows: 1, width: "80%" }} active />
                    </div>
                }
                renderItem={(item) => {
                    return (
                        <ListItem
                            key={item._id}
                            actions={[
                                <Button
                                    key={0}
                                    type="link"
                                    onClick={() => {
                                        PersistentModels.getInstance()
                                            .getRouter()
                                            .duplicateProjectInAudioEditor(item._id, true);
                                    }}
                                >
                                    <i className="pe-7s-note text-xl my-0 mx-2.5" />
                                    <div className="hidden lg:inline-block">
                                        {window.getI18n(ti18n, "DUPLICATE_AND_EDIT").toUpperCase()}
                                    </div>
                                </Button>,
                            ]}
                        >
                            <ListItemMeta
                                title={
                                    <span className="py-0 px-1">
                                        {moment(new Date(item.createdDate)).format("MMMM D, YYYY h:mm A")}
                                        {isInWorkspace && (
                                            <span className="ml-2">
                                                <VisibilityBadge visibility={item.visibility || "private"} />
                                            </span>
                                        )}
                                    </span>
                                }
                                description={<span className="py-0 px-1">Version {item.version}</span>}
                            />
                        </ListItem>
                    );
                }}
                pageSize={props.pageSize}
            />
        </div>
    );
};

UserEditHistoryList.propTypes = {
    containerHeightMargin: PropTypes.number,
    pageSize: PropTypes.number,
    searchFieldValue: PropTypes.string,
};

export default UserEditHistoryList;
